import { Box, FormControlLabel, Radio } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import type { Control } from "react-hook-form"
import { useFieldArray, useFormContext } from "react-hook-form"

import type { ShipFromTransitTimeFormValues } from "../../schemas/shipFromTransitTime"

import { Button } from "@/components/common/Button"
import { FormNumericField, FormSelect } from "@/components/UiKit/FormControls"
import { useLookupWarehouses } from "@/hooks/useLookupWarehouses"

interface ShipFromTransitTimeFormProps {
  control: Control<ShipFromTransitTimeFormValues>
  onWarehouseChange: () => void
}

export const ShipFromTransitTimeForm: FC<ShipFromTransitTimeFormProps> = ({ control, onWarehouseChange }) => {
  const { setValue, watch } = useFormContext<ShipFromTransitTimeFormValues>()
  const { warehouses, isFetching: warehousesLoading } = useLookupWarehouses()
  const { fields, remove, append } = useFieldArray({
    control,
    name: "rows",
  })

  const rows = watch("rows")

  const handleDeleteRow = (index: number) => {
    remove(index)
  }

  const handleAddRow = () => {
    append({
      shipFromWarehouseId: "",
      estimatedTransitDays: null,
      isPreferred: false,
    })
  }

  const handlePreferredChange = (preferredIndex: number) => {
    fields.forEach((_, index) => {
      setValue(`rows.${index}.isPreferred`, index === preferredIndex)
    })
  }

  const warehouseOptions = warehouses.map((wh) => ({
    label: wh.label,
    value: wh.id,
  }))

  return (
    <>
      <Box className='space-y-4'>
        {fields.map((field, index) => (
          <Box key={field.id} className='flex items-start gap-3' data-test='ship-from-row'>
            <Box className='grid flex-1 grid-cols-2 gap-3'>
              <Box>
                <FormSelect
                  control={control}
                  name={`rows.${index}.shipFromWarehouseId`}
                  label='Ship From'
                  placeholder='Select warehouse'
                  options={warehouseOptions}
                  required
                  disabled={warehousesLoading}
                  data-testid={`ship-from-warehouse-${index}`}
                  onChange={onWarehouseChange}
                />
              </Box>
              <Box>
                <FormNumericField
                  control={control}
                  name={`rows.${index}.estimatedTransitDays`}
                  label='Estimated Transit Days'
                  placeholder='Enter days'
                  data-testid={`ship-from-transit-days-${index}`}
                />
              </Box>
            </Box>
            <Box className='mt-6 flex items-center gap-2'>
              <FormControlLabel
                control={
                  <Radio
                    checked={rows[index]?.isPreferred ?? false}
                    onChange={() => handlePreferredChange(index)}
                    data-testid={`ship-from-preferred-${index}`}
                  />
                }
                label='Preferred'
              />
              <Button
                appearance='text'
                size='small'
                onClick={() => handleDeleteRow(index)}
                data-testid={`ship-from-delete-${index}`}
              >
                <DeleteOutlined className='text-gray-600' />
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      <Box className='mt-4 flex justify-end'>
        <Button
          appearance='text'
          size='small'
          onClick={handleAddRow}
          className='text-sm text-primary'
          data-testid='ship-from-add-button'
        >
          + Additional Ship From / Transit Time
        </Button>
      </Box>
    </>
  )
}
